import React from 'react';
import { CustomPaletteColor } from 'theme';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slide,
} from "@mui/material";

import { PanelLeftClose, ChevronsLeft, ChevronsRight } from 'lucide-react';

import {
  ChevronRightOutlined,
  ExpandMoreOutlined,
} from "@mui/icons-material";
import { LockKeyhole } from 'lucide-react';

import { SideBarItems } from '../Sidebar';
import { getSideBarMenuItems } from 'SiteMenu';

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FlexBetween } from '../FlexBetween';

import { Logger } from "../../utils/Logger";
import { useSelector } from "react-redux";
import { RootState } from "../../state/store";
import { ProfileImage } from './ProfileImage';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { selectLabels } from 'state/dictionary/dictionarySlice';
import { hasInstantUpdateBusinessPlusOrEnterprise } from 'utils/productPlan';

/** `isSidebarOpen` is driven globally from `pages/Layout/Layout.tsx` (auto-collapsed when viewport is under 1200px). */
interface SidebarProps {
  setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isSidebarOpen: boolean;
  isSidebarMinified: boolean;
  setIsSidebarMinified: React.Dispatch<React.SetStateAction<boolean>>;
  drawerWidth: number;
  isNonMobile: boolean;
  isSuperAdmin: boolean; // New prop
}


export const Sidebar: React.FC<SidebarProps> = ({ setIsSidebarOpen, isSidebarOpen, isSidebarMinified, setIsSidebarMinified, drawerWidth, isNonMobile }) => {
  const loggerMode = useSelector((state: RootState) => state.logger.value);
  const clientConfigMenuKey = useSelector((state: RootState) => state.clientConfig.data?.menuKey);
  const clientConfigManagePlan = useSelector((state: RootState) => state.clientConfig.data?.managePlan);
  const labels = useSelector((state: RootState) => selectLabels(state));
  const { pathname } = useLocation();
  const [active, setActive] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const navigate = useNavigate();
  const theme = useTheme();
  const navColors = theme.palette.navigation;
  const menuItemColor = navColors?.menuItemColor ?? (theme.palette.secondary as CustomPaletteColor)[100];
  const menuItemIconColor = navColors?.menuItemIconColor ?? (theme.palette.secondary as CustomPaletteColor)[200];
  const menuItemHoverColor = navColors?.menuItemHoverColor;
  const user = useSelector((state: RootState) => state.authentication.user); // Kept for ProfileImage and general user data
  const company = useSelector((state: RootState) => state.authentication.company);
  const { hasInstantUpdateAccess, hasAutomateTestAccess, hasAssetManagementAccess, hasInfralytiqsAccess, hasAnamnesisCloudAccess, hasBeamonScreenAccess, isAdminInAnyModule, canManageUsersAndCompanies, isUserSuperAdmin } = useModulePermissions();

  useEffect(() => {
    setActive(pathname);
  }, [pathname]);

  const activeHeaderKey = useMemo(() => {
    const menuItems = getSideBarMenuItems(clientConfigMenuKey);
    for (const item of menuItems) {
      if (item.type !== "menuHeader" || !item.items?.length) {
        continue;
      }
      const hasActiveChild = item.items.some((child) => {
        const childPath = `${child.path}${child.urlKey}`;
        return pathname === childPath || pathname.startsWith(`${childPath}/`);
      });
      if (hasActiveChild) {
        return item.urlKey;
      }
    }
    return null;
  }, [clientConfigMenuKey, pathname]);

  // Auto-expand the section that owns the active route (initial load + navigation).
  // Manual collapse still works because expanded state is only driven by expandedKeys.
  useEffect(() => {
    if (!activeHeaderKey) return;
    setExpandedKeys((prev) =>
      prev.includes(activeHeaderKey) ? prev : [...prev, activeHeaderKey],
    );
  }, [pathname, activeHeaderKey]);

  const handleAccordionChange = (urlKey: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedKeys((prev) => {
      if (isExpanded) {
        return prev.includes(urlKey) ? prev : [...prev, urlKey];
      }
      return prev.filter((key) => key !== urlKey);
    });
  };

  // Helper: check if the company has an active product plan for a given module.
  // Validates status is "active" AND the current date falls within startedAt..endsAt.
  // If company data hasn't loaded yet, returns true so modules aren't blocked;
  // the sidebar will re-render once company data arrives from the API.
  const hasActiveProductPlan = (moduleName: string): boolean => {
    if (!company) return true;
    const plans = (company as any)?.productPlans || [];
    const now = new Date();
    return Array.isArray(plans) && plans.some((plan: any) => {
      if (!plan) return false;
      const sameModule = String(plan.module).toLowerCase() === moduleName.toLowerCase();
      const activeStatus = String(plan.status || "").toLowerCase() === "active";
      if (!sameModule || !activeStatus) return false;
      // Validate date range
      const startedAt = plan.startedAt ? new Date(plan.startedAt) : null;
      const endsAt = plan.endsAt ? new Date(plan.endsAt) : null;
      if (startedAt && now < startedAt) return false;
      if (endsAt && now > endsAt) return false;
      return true;
    });
  };

  const hasActiveProductPlanWithLicenseType = (licenseType: string): boolean => {
    if (!company) return true;
    const plans = (company as any)?.productPlans || [];
    const now = new Date();
    return Array.isArray(plans) && plans.some((plan: any) => {
      if (!plan) return false;
      const matchesLicense = String(plan.licenseType || "").toLowerCase().includes(licenseType.toLowerCase());
      const activeStatus = String(plan.status || "").toLowerCase() === "active";
      if (!matchesLicense || !activeStatus) return false;
      const startedAt = plan.startedAt ? new Date(plan.startedAt) : null;
      const endsAt = plan.endsAt ? new Date(plan.endsAt) : null;
      if (startedAt && now < startedAt) return false;
      if (endsAt && now > endsAt) return false;
      return true;
    });
  };

  const hasActiveProductPlanWithAnyLicenseType = (licenseTypes: string[]): boolean => {
    if (!company) return true;
    return licenseTypes.some(lt => hasActiveProductPlanWithLicenseType(lt));
  };

  Logger.info("st-ck-client/src/components/Sidebar/Sidebar.tsx", "Sidebar rendered");

  return (
    <Box component="nav">
      <Drawer
        open={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        variant={isNonMobile && isSidebarOpen ? "persistent" : "temporary"}
        anchor="left"
        sx={{
          width: drawerWidth,
          "& .MuiDrawer-paper": {
            color: menuItemIconColor,
            backgroundColor: theme.palette.background.paper,
            boxSizing: "border-box",
            borderWidth: isNonMobile ? 0 : "2px",
            width: drawerWidth,
            overflowX: "hidden",
          },
        }}
      >
        <Box width="100%">
          {/* Header section with same height as navbar (100px) */}
          <Box sx={{ 
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: isSidebarMinified ? 'flex-start' : 'center',
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'relative'
          }}>
            {/* Close button positioned absolutely in top-right */}
            <IconButton 
              onClick={() => setIsSidebarOpen(false)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                display: isSidebarMinified ? 'none' : undefined,
              }}
            >
              <PanelLeftClose />
            </IconButton>

            {!isSidebarMinified && isNonMobile && (
              <IconButton
                onClick={() => setIsSidebarMinified(true)}
                aria-label="Show icons only"
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  zIndex: 1,
                }}
              >
                <ChevronsLeft />
              </IconButton>
            )}

            {isSidebarMinified ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  pt: '6px',
                  pb: '12px',
                  gap: 0.5,
                  width: '100%',
                }}
              >
                <IconButton
                  onClick={() => setIsSidebarMinified(false)}
                  aria-label="Expand sidebar"
                >
                  <ChevronsRight />
                </IconButton>
                <Tooltip
                  title={user?.profile?.lastName || user?.profile?.firstName || 'User'}
                  placement="right"
                  enterDelay={300}
                >
                  <Box component="span" sx={{ display: 'inline-flex', lineHeight: 0 }}>
                    <ProfileImage user={user} size={32} />
                  </Box>
                </Tooltip>
              </Box>
            ) : (
              <>
                {/* Centered User Avatar */}
                <Box display="flex" justifyContent="center" alignItems="center" mb={1}>
                  <ProfileImage user={user}/>
                </Box>
                
                {/* Centered Greeting Message */}
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Typography variant="body1" fontWeight="bold" textAlign="center" sx={{ fontSize: '1rem' }}>
                    Hello, {user?.profile?.lastName || user?.profile?.firstName || "User"}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {getSideBarMenuItems(clientConfigMenuKey).map(({ path, text, urlKey, icon, type, items, requiredModule, dictionaryKey, requiredRole, allowedDomains }) => {
            // Filter menu items based on module permissions
            let filteredItems = items;
            
            if (type === "menuHeader") {
              // For menu headers, apply role/domain restrictions if provided (e.g., Administration)
              if (requiredRole === 'superAdmin') {
                const emailDomain = (user?.email || '').split('@')[1]?.toLowerCase();
                const domainAllowed = !allowedDomains || (emailDomain ? allowedDomains.includes(emailDomain) : false);
                if (!isUserSuperAdmin || !domainAllowed) {
                  return null;
                }
              }
              if (requiredRole === 'admin' && !requiredModule) {
                if (!isAdminInAnyModule()) {
                  return null;
                }
              }
              // Manage Plan visibility is controlled by the per-domain `managePlan` flag.
              // Hidden only when explicitly false; absent/true keeps it visible.
              if (urlKey === "manage-plan" && clientConfigManagePlan === false) {
                return null;
              }
              // AutomateTest module menu filtering by user module access AND product plan
              if (requiredModule === "automatetest.ai") {
                if (!hasAutomateTestAccess() && !isUserSuperAdmin) {
                  return null;
                }
                if (!hasActiveProductPlan("automatetest.ai")) {
                  return null;
                }
              }
              // AssetManagement module menu filtering by user module access AND product plan
              if (requiredModule === "assetsmanagement.ai") {
                if (!hasAssetManagementAccess() && !isUserSuperAdmin) {
                  return null;
                }
                if (!hasActiveProductPlan("assetsmanagement.ai")) {
                  return null;
                }
              }
              // Infralytiqs module menu filtering by user module access AND product plan
              if (requiredModule === "infralytiqs.com") {
                if (!hasInfralytiqsAccess() && !isUserSuperAdmin) {
                  return null;
                }
                if (!hasActiveProductPlan("infralytiqs.com")) {
                  return null;
                }
              }
              // Anamnesis module menu filtering by user module access ONLY (no license-type check)
              if (requiredModule === "anamnesis.cloud") {
                if (!hasAnamnesisCloudAccess() && !isUserSuperAdmin) {
                  return null;
                }
              }
              // Bean On Screen module menu filtering by user module access ONLY (no license-type check)
              if (requiredModule === "beamonscreen.com") {
                if (!hasBeamonScreenAccess() && !isUserSuperAdmin) {
                  return null;
                }
              }
              if (urlKey === "settings") {
                // Filter settings menu items based on permissions
                filteredItems = items?.filter(item => {
                  // Check for super admin role requirement
                  if (item.requiredRole === "superAdmin") {
                    const emailDomain = (user?.email || '').split('@')[1]?.toLowerCase();
                    const domainAllowed = !item.allowedDomains || (emailDomain ? item.allowedDomains.includes(emailDomain) : false);
                    return (canManageUsersAndCompanies() || isUserSuperAdmin) && domainAllowed;
                  }
                  
                  // Check for module access requirement with specific role
                  if (item.requiredModule && item.requiredRole) {
                    // For instantupdate.ai module with admin role requirement
                    if (item.requiredModule === "instantupdate.ai" && item.requiredRole === "admin") {
                      // Only show to users with "admin" role in instantupdate.ai module
                      return hasInstantUpdateAccess("admin") || isUserSuperAdmin;
                    }
                    // For other module/role combinations, use general module access
                    return hasInstantUpdateAccess() || isUserSuperAdmin;
                  }
                  
                  // Check for module access requirement without specific role
                  if (item.requiredModule) {
                    return hasInstantUpdateAccess() || isUserSuperAdmin;
                  }
                  
                  return true;
                });
              } else if (urlKey === "instant-updates") {
                // Only show Campaigns/Instant Update menu if user has instantupdate.ai access or is superadmin
                if (!hasInstantUpdateAccess() && !isUserSuperAdmin) {
                  return null;
                }
                if (!hasActiveProductPlan("instantupdate.ai")) {
                  return null;
                }
              }

              return (
                <Accordion
                  key={urlKey}
                  expanded={expandedKeys.includes(urlKey)}
                  onChange={handleAccordionChange(urlKey)}
                  disableGutters={isSidebarMinified}
                  sx={{
                    m: isSidebarMinified ? "6px 8px" : "0.5rem 0.5rem 0.5rem 0.5rem",
                    "&.MuiAccordion-root.Mui-expanded": {
                      margin: isSidebarMinified ? "6px 8px" : 0,
                    },
                    ...(isSidebarMinified && {
                      "&:before": { display: "none" },
                      "&&": {
                        margin: "6px 8px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundImage: "none",
                        backgroundColor: theme.palette.mode === "dark"
                          ? (theme.palette.primary as CustomPaletteColor)[400]
                          : "rgba(0,0,0,0.04)",
                        border: `1px solid ${theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.18)"
                          : "rgba(0,0,0,0.12)"}`,
                        boxShadow: theme.palette.mode === "dark"
                          ? "0 1px 3px rgba(0,0,0,0.45)"
                          : "0 1px 3px rgba(0,0,0,0.12)",
                      },
                      "&&.Mui-expanded": {
                        margin: "6px 8px",
                        backgroundColor: theme.palette.mode === "dark"
                          ? "rgba(255, 227, 163, 0.16)"
                          : "rgba(204, 167, 82, 0.14)",
                        borderColor: theme.palette.mode === "dark"
                          ? "rgba(255, 227, 163, 0.35)"
                          : "rgba(204, 167, 82, 0.4)",
                        boxShadow: theme.palette.mode === "dark"
                          ? "0 2px 6px rgba(0,0,0,0.45)"
                          : "0 2px 6px rgba(0,0,0,0.14)",
                      },
                    }),
                  }}>
                  <AccordionSummary
                    expandIcon={isSidebarMinified ? null : <ExpandMoreOutlined />}
                    aria-controls={`${urlKey}-content`}
                    id={`${urlKey}--header`}
                    sx={{
                      backgroundColor: "transparent",
                      color: menuItemColor,
                      "& .MuiAccordionSummary-content": {
                        margin: 0,
                        padding: 0,
                        justifyContent: isSidebarMinified ? "center" : "flex-start",
                      },
                      "& .MuiAccordionSummary-content p": {
                        margin: 0,
                        paddingLeft: isSidebarMinified ? 0 : "2rem",
                        paddingTop: "0.5rem",
                        paddingBottom: "0.5rem",
                        display: isSidebarMinified ? "none" : undefined,
                      },
                      "& .MuiAccordionSummary-content.Mui-expanded": {
                        margin: 0,
                      },
                      "&.Mui-expanded": {
                        minHeight: isSidebarMinified ? 40 : "0px",
                      },
                      ...(isSidebarMinified && {
                        minHeight: 40,
                        maxHeight: 40,
                        px: 0,
                        py: 0,
                        justifyContent: "center",
                        "& .MuiAccordionSummary-expandIconWrapper": {
                          display: "none",
                        },
                      }),
                    }}
                  >
                    <FlexBetween sx={{
                        ml: "0rem",
                        mt: isSidebarMinified ? 0 : "0.5rem",
                        mb: isSidebarMinified ? 0 : "0.5rem",
                        color: menuItemIconColor,
                        width: isSidebarMinified ? "100%" : undefined,
                        justifyContent: isSidebarMinified ? "center" : "space-between",
                      }}>
                      {isSidebarMinified ? (
                        <Tooltip title={labels?.[dictionaryKey ?? ""] ?? text} placement="right" enterDelay={300}>
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            {icon}
                          </Box>
                        </Tooltip>
                      ) : icon}
                      <Typography key={urlKey} sx={{ m: "2.25rem 0 1rem 3rem" }}>
                        {labels?.[dictionaryKey ?? ""] ?? text}
                      </Typography>
                    </FlexBetween>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      "&.MuiAccordionDetails-root": {
                        padding: 0,
                        ...(isSidebarMinified && {
                          paddingBottom: "16px",
                        }),
                      },
                      ...(isSidebarMinified && {
                        "& .MuiListItem-root": {
                          minHeight: 40,
                        },
                      }),
                    }}>
                    {filteredItems && filteredItems.map((menuItem: SideBarItems) => {
                      // Guard super admin only items inside any menu header (e.g., Administration)
                      if (menuItem.requiredRole === 'superAdmin') {
                        const emailDomain = (user?.email || '').split('@')[1]?.toLowerCase();
                        const domainAllowed = !menuItem.allowedDomains || (emailDomain ? menuItem.allowedDomains.includes(emailDomain) : false);
                        if (!isUserSuperAdmin || !domainAllowed) {
                          return null;
                        }
                      }
                      // Filter individual menu items based on permissions
                      if (menuItem.requiredModule && menuItem.requiredRole) {
                        if (menuItem.requiredModule === "instantupdate.ai") {
                          if (menuItem.requiredRole === "admin") {
                            if (!hasInstantUpdateAccess("admin") && !isUserSuperAdmin) {
                              return null;
                            }
                          } else if (menuItem.requiredRole === "user") {
                            if (!hasInstantUpdateAccess("user") && !hasInstantUpdateAccess("admin") && !isUserSuperAdmin) {
                              return null;
                            }
                          }
                        } else if (menuItem.requiredModule === "infralytiqs.com" && menuItem.requiredRole === "admin") {
                          if (!hasInfralytiqsAccess("admin") && !isUserSuperAdmin) {
                            return null;
                          }
                        } else if (menuItem.requiredModule === "anamnesis.cloud") {
                          if (!hasAnamnesisCloudAccess(menuItem.requiredRole === "admin" ? "admin" : undefined) && !isUserSuperAdmin) {
                            return null;
                          }
                        } else if (menuItem.requiredModule === "beamonscreen.com") {
                          if (!hasBeamonScreenAccess(menuItem.requiredRole === "admin" ? "admin" : undefined) && !isUserSuperAdmin) {
                            return null;
                          }
                        }
                      }

                      // Module-only checks (no specific role required)
                      if (menuItem.requiredModule && !menuItem.requiredRole) {
                        if (menuItem.requiredModule === "anamnesis.cloud" && !hasAnamnesisCloudAccess() && !isUserSuperAdmin) {
                          return null;
                        }
                        if (menuItem.requiredModule === "beamonscreen.com" && !hasBeamonScreenAccess() && !isUserSuperAdmin) {
                          return null;
                        }
                      }

                      if (menuItem.requiredLicenseType) {
                        const userStatus = String((user as any)?.status || "").toLowerCase();
                        if (userStatus !== "active" || !hasActiveProductPlanWithLicenseType(menuItem.requiredLicenseType)) {
                          return null;
                        }
                      }

                      if (menuItem.requiresBusinessPlusOrEnterprise && !hasInstantUpdateBusinessPlusOrEnterprise(company)) {
                        return null;
                      }

                      const isPremiumLocked = menuItem.premiumLicenseTypes
                        ? !hasActiveProductPlanWithAnyLicenseType(menuItem.premiumLicenseTypes)
                        : false;
                      const isDisabled = menuItem.disabled === true || isPremiumLocked;
                      const lockedBadgeLabel =
                        menuItem.comingSoonBadge === true || menuItem.menuKey === 'instantUpdate'
                          ? 'Coming soon'
                          : 'Premium';
                      const itemPath = menuItem.path + menuItem.urlKey;
                      const isSelected = active === itemPath || active.startsWith(`${itemPath}/`);
                      return (
                        <ListItem key={menuItem.urlKey} disablePadding>
                          <ListItemButton
                            onClick={() => {
                              if (isDisabled) return;
                              navigate(`${menuItem.path}${menuItem.urlKey}`);
                              setActive(`${menuItem.path}${menuItem.urlKey}`);
                              if (!isNonMobile) {
                                setIsSidebarOpen(false);
                              }
                            }}
                            sx={{
                              border: isSidebarMinified ? "none" : "1px solid transparent",
                              borderRadius: 1,
                              backgroundColor: isSidebarMinified
                                  ? "transparent"
                                  : isSelected
                                    ? (theme.palette.secondary as CustomPaletteColor)[300]
                                    : "transparent",
                              color: isSelected
                                  ? (theme.palette.primary as CustomPaletteColor)[600]
                                  : menuItemColor,
                              "&.MuiListItemButton-root": {
                                padding: isSidebarMinified ? 0 : "0.5rem 0",
                                minHeight: isSidebarMinified ? 40 : undefined,
                                height: isSidebarMinified ? 40 : undefined,
                                justifyContent: isSidebarMinified ? "center" : undefined,
                                pl: 0,
                              },
                              /* Outlined hover for both selected and non-selected items */
                              "&:hover": {
                                backgroundColor: "transparent",
                                borderColor: "secondary.main",
                                color: menuItemHoverColor ?? "secondary.main",
                                "& .MuiListItemIcon-root": {
                                  color: menuItemHoverColor ?? "secondary.main",
                                },
                                "& .MuiListItemText-primary": {
                                  color: menuItemHoverColor ?? "secondary.main",
                                },
                                "& .MuiSvgIcon-root": {
                                  color: menuItemHoverColor ?? "secondary.main",
                                },
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                ml: isSidebarMinified ? 0 : "1.5rem",
                                minWidth: isSidebarMinified ? 0 : undefined,
                                justifyContent: isSidebarMinified ? "center" : undefined,
                                color: isSelected
                                    ? (theme.palette.primary as CustomPaletteColor)[600]
                                    : menuItemIconColor,
                              }}
                            >
                              {isSidebarMinified ? (
                                <Tooltip title={labels?.[menuItem.dictionaryKey ?? ""] ?? menuItem.text} placement="right" enterDelay={300}>
                                  <Box
                                    component="span"
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 36,
                                      height: 36,
                                      borderRadius: 1,
                                      backgroundColor: isSelected
                                        ? (theme.palette.secondary as CustomPaletteColor)[300]
                                        : theme.palette.mode === 'dark'
                                          ? 'rgba(255,255,255,0.14)'
                                          : 'rgba(0,0,0,0.08)',
                                    }}
                                  >
                                    {menuItem.icon}
                                  </Box>
                                </Tooltip>
                              ) : menuItem.icon}
                            </ListItemIcon>
                            <ListItemText 
                              primary={labels?.[menuItem.dictionaryKey ?? ""] ?? menuItem.text}
                              secondaryTypographyProps={{ component: 'span' }}
                              sx={{ display: isSidebarMinified ? 'none' : undefined }}
                              secondary={isDisabled ? (
                                <Box
                                  component="span"
                                  sx={{
                                    mt: 0.5,
                                    ml: 0,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: '8px',
                                    backgroundColor: 'rgb(255, 215, 0)',
                                    width: 'fit-content'
                                  }}
                                >
                                  <LockKeyhole size={14} color={'rgb(139, 69, 19)'} />
                                  <Typography component="span" variant="caption" sx={{ color: 'rgb(139, 69, 19)', fontWeight: 700 }}>{lockedBadgeLabel}</Typography>
                                </Box>
                              ) : undefined}
                            />
                            {isSelected && !isSidebarMinified && (
                              <ChevronRightOutlined sx={{ ml: "auto" }} />
                            )}
                          </ListItemButton>
                        </ListItem>
                      );
                    }
                    )}
                  </AccordionDetails>

                </Accordion>

              );
            } else {
              // For single menu items (outside of accordion), apply role/domain restrictions if provided
              if (requiredRole === 'superAdmin') {
                const emailDomain = (user?.email || '').split('@')[1]?.toLowerCase();
                const domainAllowed = !allowedDomains || (emailDomain ? allowedDomains.includes(emailDomain) : false);
                if (!isUserSuperAdmin || !domainAllowed) {
                  return null;
                }
              }
              //DISPLAY ONLY SINGLE MENU ITEM WHICH IS OUTSIDE OF THE ACCORDION
              const isTopLevelSelected = active === path + urlKey;
              return (
                <ListItem
                  key={urlKey}
                  disablePadding
                  sx={isSidebarMinified ? {
                    mx: "8px",
                    my: "6px",
                    width: "auto",
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: theme.palette.mode === "dark"
                      ? (theme.palette.primary as CustomPaletteColor)[400]
                      : "rgba(0,0,0,0.04)",
                    border: `1px solid ${theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.18)"
                      : "rgba(0,0,0,0.12)"}`,
                    boxShadow: theme.palette.mode === "dark"
                      ? "0 1px 3px rgba(0,0,0,0.45)"
                      : "0 1px 3px rgba(0,0,0,0.12)",
                  } : undefined}
                >
                  <ListItemButton
                    onClick={() => {
                      navigate(`${path}${urlKey}`);
                      setActive(path+urlKey);
                      // Close sidebar on mobile after navigation
                      if (!isNonMobile) {
                        setIsSidebarOpen(false);
                      }
                    }}
                    sx={{
                      border: isSidebarMinified ? "none" : "1px solid transparent",
                      borderRadius: 1,
                      backgroundColor: isTopLevelSelected
                          ? (theme.palette.secondary as CustomPaletteColor)[300]
                          : "transparent",
                      color: isTopLevelSelected
                          ? (theme.palette.primary as CustomPaletteColor)[600]
                          : menuItemColor,
                      "&.MuiListItemButton-root": {
                        justifyContent: isSidebarMinified ? "center" : undefined,
                        px: isSidebarMinified ? 0 : undefined,
                        py: isSidebarMinified ? 0 : undefined,
                        minHeight: isSidebarMinified ? 40 : undefined,
                        height: isSidebarMinified ? 40 : undefined,
                      },
                      "&:hover": {
                        backgroundColor: "transparent",
                        borderColor: "secondary.main",
                        color: menuItemHoverColor ?? "secondary.main",
                        "& .MuiListItemIcon-root": {
                          color: menuItemHoverColor ?? "secondary.main",
                        },
                        "& .MuiListItemText-primary": {
                          color: menuItemHoverColor ?? "secondary.main",
                        },
                        "& .MuiSvgIcon-root": {
                          color: menuItemHoverColor ?? "secondary.main",
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        ml: "0rem",
                        padding: "0.5rem",
                        minWidth: isSidebarMinified ? 40 : undefined,
                        justifyContent: isSidebarMinified ? "center" : undefined,
                        color: isTopLevelSelected
                            ? (theme.palette.primary as CustomPaletteColor)[600]
                            : menuItemIconColor,
                      }}
                    >
                      {isSidebarMinified ? (
                        <Tooltip title={labels?.[dictionaryKey ?? ""] ?? text} placement="right" enterDelay={300}>
                          <Box component="span" sx={{ display: 'inline-flex' }}>
                            {icon}
                          </Box>
                        </Tooltip>
                      ) : icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={labels?.[dictionaryKey ?? ""] ?? text}
                      sx={{ display: isSidebarMinified ? 'none' : undefined }}
                    />
                    {isTopLevelSelected && !isSidebarMinified && (
                      <ChevronRightOutlined sx={{ ml: "auto" }} />
                    )}
                  </ListItemButton>
                </ListItem>
              );
            }
          })}
        </Box>
      </Drawer>
    </Box>
  );

};
